import React from 'react';
import { Box, Grid, Card, useTheme, useMediaQuery, Divider, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventNoteIcon from '@mui/icons-material/EventNote';

// Styled component for the animated skeleton with customizable properties
const SkeletonElement = styled(Box)(({ 
  width = '100%', 
  height = '24px',
  borderRadius = '4px',
  marginBottom = 0
}) => ({
  height,
  backgroundColor: 'rgba(200, 200, 200, 0.7)',
  borderRadius,
  width,
  marginBottom,
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
    animation: 'shimmer 1.5s infinite',
  },
  '@keyframes shimmer': {
    '0%': {
      transform: 'translateX(-100%)',
    },
    '100%': {
      transform: 'translateX(100%)',
    },
  },
}));

// Header section for desktop view
const DesktopHeaderSkeleton = () => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
    <Box>
      <SkeletonElement width="180px" height="32px" marginBottom={1} />
      <SkeletonElement width="220px" height="20px" />
    </Box>
    <SkeletonElement width="150px" height="36px" borderRadius="20px" />
  </Box>
);

// Column headers for desktop view
const DesktopColumnHeadersSkeleton = () => {
  const headers = [
    { width: 3, icon: <CalendarTodayIcon fontSize="small" /> },
    { width: 2, icon: <InfoOutlinedIcon fontSize="small" /> },
    { width: 1, icon: <AttachMoneyIcon fontSize="small" /> },
    { width: 1, icon: <QrCodeIcon fontSize="small" /> },
    { width: 4, icon: <LocationOnIcon fontSize="small" /> },
    { width: 1, icon: null }
  ];

  return (
    <Box sx={{
      backgroundColor: 'grey.50',
      borderRadius: 1,
      mb: 2,
      p: 1
    }}>
      <Grid container spacing={2}>
        {headers.map((header, index) => (
          <Grid item xs={header.width} key={index}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.6 }}>
              {header.icon}
              <SkeletonElement height="20px" width="60%" />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// A single desktop booking row skeleton
const DesktopBookingRowSkeleton = ({ isFirst = false, theme }) => {
  const expandedFirst = isFirst;
  
  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        backgroundColor: 'background.paper',
        borderRadius: 1,
        boxShadow: theme.shadows[1],
        border: expandedFirst ? `1px solid ${theme.palette.divider}` : 'none',
        borderBottomLeftRadius: expandedFirst ? 0 : undefined,
        borderBottomRightRadius: expandedFirst ? 0 : undefined,
        borderBottom: expandedFirst ? 'none' : undefined,
      }}
    >
      {expandedFirst && (
        <Box sx={{ mb: 2 }}>
          <SkeletonElement width="60%" height="20px" marginBottom={2} />
        </Box>
      )}
      
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={3}>
          <SkeletonElement width="90%" />
        </Grid>
        <Grid item xs={2}>
          <SkeletonElement width="80%" height="28px" borderRadius="12px" />
        </Grid>
        <Grid item xs={1}>
          <SkeletonElement width="70%" />
        </Grid>
        <Grid item xs={1}>
          <SkeletonElement width="60%" />
        </Grid>
        <Grid item xs={4}>
          <SkeletonElement width="90%" />
        </Grid>
        <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <SkeletonElement width="80px" height="32px" borderRadius="4px" />
        </Grid>
      </Grid>
      
      {expandedFirst && (
        <Box
          sx={{
            p: 3,
            backgroundColor: 'background.paper',
            borderBottomLeftRadius: theme.shape.borderRadius,
            borderBottomRightRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[0],
            mb: 1,
            mt: 0,
            border: '0px solid',
            borderTopColor: 'divider',
          }}
        >
          <Grid container spacing={2}>
            {/* Left Column (Venue Information) */}
            <Grid item xs={9}>
              <SkeletonElement width="50%" height="28px" marginBottom={2} />
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ mr: 1.5, opacity: 0.6 }}>
                  <LocationOnIcon fontSize="small" />
                </Box>
                <SkeletonElement width="80%" />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ mr: 1.5, mt: 0.3, opacity: 0.6 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </Box>
                <SkeletonElement width="70%" />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ mr: 1.5, mt: 0.3, opacity: 0.6 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </Box>
                <Box sx={{ width: '100%' }}>
                  <SkeletonElement width="40%" height="20px" marginBottom={1} />
                  <SkeletonElement width="90%" height="20px" />
                </Box>
              </Box>
            </Grid>
            
            {/* Right Column (Actions) */}
            <Grid item xs={3} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <SkeletonElement width="100%" height="36px" borderRadius="4px" />
              <SkeletonElement width="100%" height="36px" borderRadius="4px" />
              <SkeletonElement width="100%" height="36px" borderRadius="4px" />
              <SkeletonElement width="100%" height="36px" borderRadius="4px" />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

// Empty state skeleton for desktop
const DesktopEmptyStateSkeleton = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px',
      backgroundColor: 'grey.50',
      border: '2px dashed',
      borderColor: 'divider',
      boxShadow: 'none',
      gap: 2,
      position: 'relative',
      overflow: 'hidden',
      padding: 2,
      borderRadius: 1,
    }}
  >
    {/* Icon Section */}
    <Box
      sx={{
        width: '25%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: 'rgba(200, 200, 200, 0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
            animation: 'shimmer 1.5s infinite',
          },
        }}
      />
    </Box>
    
    {/* Content Section */}
    <Box
      sx={{
        width: '75%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2
      }}
    >
      <SkeletonElement width="60%" height="24px" marginBottom={1} />
      <SkeletonElement width="80%" height="20px" marginBottom={2} />
      <SkeletonElement width="180px" height="40px" borderRadius="20px" />
    </Box>
  </Box>
);

// Mobile header skeleton
const MobileHeaderSkeleton = () => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    px: 2,
    mb: 2
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ opacity: 0.6 }}>
        <EventNoteIcon sx={{ fontSize: '1.25rem' }} />
      </Box>
      <SkeletonElement width="120px" height="24px" />
    </Box>
    <SkeletonElement width="60px" height="20px" />
  </Box>
);

// Single mobile booking card skeleton
const MobileBookingCardSkeleton = ({ isFirst = false, theme }) => {
  const expandedFirst = isFirst;
  
  return (
    <Card
      sx={{
        overflow: 'visible',
        transition: 'all 0.2s ease',
        boxShadow: theme.shadows[1],
        borderRadius: 2,
        mb: 2
      }}
    >
      {expandedFirst && (
        <Box sx={{ p: 2, pb: 0 }}>
          <SkeletonElement width="80%" height="20px" marginBottom={2} />
          
          <SkeletonElement width="70%" height="24px" marginBottom={2} />
          
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box sx={{ opacity: 0.6, mt: 0.3 }}>
                <LocationOnIcon fontSize="small" />
              </Box>
              <SkeletonElement width="90%" />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box sx={{ opacity: 0.6, mt: 0.3 }}>
                <InfoOutlinedIcon fontSize="small" />
              </Box>
              <SkeletonElement width="80%" />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
              <Box sx={{ opacity: 0.6, mt: 0.3 }}>
                <InfoOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ width: '100%' }}>
                <SkeletonElement width="50%" height="20px" marginBottom={1} />
                <SkeletonElement width="90%" height="20px" />
              </Box>
            </Box>
            
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <SkeletonElement width="100%" height="36px" borderRadius="4px" />
              <SkeletonElement width="100%" height="36px" borderRadius="4px" />
            </Stack>
            
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <SkeletonElement width="100%" height="36px" borderRadius="4px" />
              <SkeletonElement width="100%" height="36px" borderRadius="4px" />
            </Stack>
          </Stack>
          
          <Divider sx={{ mt: 2, mb: 0 }} />
        </Box>
      )}
      
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          {!expandedFirst && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <SkeletonElement width="60%" height="22px" />
              <SkeletonElement width="80px" height="24px" borderRadius="16px" />
            </Box>
          )}
          
          {expandedFirst && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <SkeletonElement width="50px" height="20px" />
              <SkeletonElement width="80px" height="24px" borderRadius="16px" />
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ opacity: 0.6 }}>
              <CalendarTodayIcon sx={{ fontSize: '1rem' }} />
            </Box>
            <SkeletonElement width="70%" height="20px" />
          </Box>
          
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 'auto'
          }}>
            <SkeletonElement width="30%" height="20px" />
            <SkeletonElement width="40%" height="20px" />
          </Box>
        </Stack>
      </Box>
    </Card>
  );
};

// Mobile empty state skeleton
const MobileEmptyStateSkeleton = () => (
  <Card
    sx={{
      p: 4,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      backgroundColor: 'grey.50',
      border: '2px dashed',
      borderColor: 'divider',
      boxShadow: 'none'
    }}
  >
    <Box sx={{
      width: 60,
      height: 60,
      borderRadius: '50%',
      backgroundColor: 'rgba(200, 200, 200, 0.3)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mb: 2,
      position: 'relative',
      overflow: 'hidden',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
        animation: 'shimmer 1.5s infinite',
      },
    }} />
    <SkeletonElement width="70%" height="24px" marginBottom={1} />
    <SkeletonElement width="90%" height="20px" marginBottom={2} />
    <SkeletonElement width="180px" height="40px" borderRadius="20px" />
  </Card>
);

// Main component that determines which skeleton to show based on screen size
const BookingSkeleton = ({ rows = 5, expandFirstBooking = false, showHeader = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  return (
    <Box>
      {isMobile ? (
        // Mobile View Skeleton
        <>
          {showHeader && <MobileHeaderSkeleton />}
          <Box sx={{ px: 2 }}>
            <Stack spacing={2}>
              {/* First few booking cards */}
              {Array.from({ length: Math.min(rows, 3) }).map((_, index) => (
                <MobileBookingCardSkeleton 
                  key={index} 
                  isFirst={index === 0 && expandFirstBooking} 
                  theme={theme} 
                />
              ))}
              
              {/* Empty state if less than requested rows */}
              {rows < 5 && <MobileEmptyStateSkeleton />}
            </Stack>
          </Box>
        </>
      ) : (
        // Desktop View Skeleton
        <>
          {showHeader && <DesktopHeaderSkeleton />}
          <DesktopColumnHeadersSkeleton />
          
          {/* Booking rows */}
          {Array.from({ length: Math.min(rows, 5) }).map((_, index) => (
            <DesktopBookingRowSkeleton 
              key={index} 
              isFirst={index === 0 && expandFirstBooking} 
              theme={theme} 
            />
          ))}
          
          {/* Empty state if less than requested rows */}
          {rows < 5 && <DesktopEmptyStateSkeleton theme={theme} />}
        </>
      )}
    </Box>
  );
};

export default BookingSkeleton;